<template>
  <BaseCard class="flex flex-col gap-4 text-gray-900">
    <div
      v-if="product.entity?.entity_id"
      class="self-start flex gap-4 items-center"
    >
      <BaseLocaleLink
        class="flex-grow-0 flex-shrink-0 w-16 h-16 overflow-hidden rounded-xl hover:opacity-70"
        :to="entityLink"
      >
        <img
          v-if="product.entity?.entity_media"
          class="w-full h-full object-cover"
          loading="lazy"
          :src="product.entity?.entity_media"
          :alt="product.entity?.entity_name || ''"
        />
        <div
          v-else
          class="w-full h-full bg-primary flex items-center justify-center text-white text-2xl font-bold uppercase"
        >
          {{ entityAbbr }}
        </div>
      </BaseLocaleLink>
      <div class="flex flex-col gap-px">
        <BaseLocaleLink
          class="text-lg inline font-bold hover:text-gray-600"
          :to="entityLink"
        >
          {{ product.entity?.entity_name }}
        </BaseLocaleLink>
        <div class="flex flex-wrap gap-x-4 items-center text-sm">
          <div
            v-if="entityStyle && entityLabel"
            class="flex gap-1 items-center"
          >
            <BaseIcon :name="entityStyle.icon" size="xs" />
            <span>{{ entityLabel }}</span>
          </div>
          <div v-if="entitySizeLabel" class="flex gap-1 items-center">
            <BaseIcon name="outline_user_group" size="xs" />
            <span>{{ entitySizeLabel }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 xl:gap-6">
      <BaseLocaleLink
        class="overflow-hidden rounded-xl aspect-video hover:opacity-70"
        :to="productLink"
      >
        <BaseImage
          v-if="product.preview?.original_url"
          loading="lazy"
          class="w-full h-full object-cover"
          :src="product.preview?.original_url"
          :alt="title"
          width="560"
          height="315"
        />
        <div
          v-else
          class="w-full h-full flex items-center justify-center bg-gray-200 text-gray-800"
        >
          <BaseIcon name="outline_shopping_cart" size="xl" />
        </div>
      </BaseLocaleLink>
      <div class="flex flex-col group-hover:opacity-70">
        <div class="border-b-2 border-primary-100 pb-2 mb-2">
          <BaseLocaleLink :to="productLink" class="hover:text-gray-600">
            <h2 class="text-lg leading-snug font-bold uppercase">
              {{ title }}
            </h2>
          </BaseLocaleLink>
        </div>
        <div class="text-sm leading-tight">
          {{ shortDescription }}
        </div>
      </div>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { type MarketplaceProduct } from '~/models/marketplace'

interface PropsInterface {
  product: MarketplaceProduct
}

const props = defineProps<PropsInterface>()

const i18n = useI18n()
const { EntityStyleMap } = useMarketplace()

const entityStyle = computed(
  () =>
    props.product?.entity?.entity_type &&
    EntityStyleMap[props.product?.entity?.entity_type]
)

const entityAbbr = computed(
  () =>
    (props.product?.entity?.entity_name &&
      entityStyle.value?.getAbbr(props.product.entity.entity_name)) ||
    ''
)

const entityLabel = computed(
  () =>
    i18n.locale.value &&
    entityStyle.value?.labelKey &&
    i18n.t(entityStyle.value.labelKey)
)

const entityLink = computed(
  () =>
    (props.product?.entity?.entity_id &&
      entityStyle.value?.getLink(props.product.entity.entity_id)) ||
    ''
)

const entitySizeLabel = computed(
  () =>
    (props.product?.entity?.entity_size &&
      i18n.locale.value &&
      entityStyle.value?.getSize(props.product.entity.entity_size)) ||
    ''
)

const productLink = computed(() => `/marketplace/products/${props.product.id}`)

const title = computed(() =>
  getFieldLocale(
    parseFieldLocales(props.product.title) || '',
    i18n.locale.value
  )
)

const shortDescription = computed(() =>
  props.product.short_description
    ? getFieldLocale(
        parseFieldLocales(props.product.short_description) || '',
        i18n.locale.value
      )
    : ''
)
</script>
